import { useEffect, useRef, useState } from 'react';
import { bt, btp } from '../../i18n';
import {
  resolveColor,
  THEME_COLOR_TOKENS,
  type ColorField,
  type ColorValue,
} from '@aquilixshop/ui-react';
import { CUSTOM_FALLBACK_HEX, themeTokenHex } from './colorUtils';
import { FieldRow } from '../FieldRow';
import { HsvColorPicker } from './HsvColorPicker';

/**
 * Color control (Shopify look): a small SQUARE swatch + the colour's SOURCE share one bordered box. The
 * source is ALWAYS a dropdown — "Accent 1 / Accent 2 / … / Custom…" — because a colour here is first a
 * CHOICE between the theme's slots and a free value, and only then a hex. Picking a slot writes
 * `{ref:'theme',token}`; picking "Custom…" writes `{ref:'custom'}` and reveals the hex field next to the
 * dropdown. The swatch opens the HSV popover (saturation square + hue + hex + eyedropper).
 *
 * The dropdown used to render ONLY in theme mode, as a native `<select>` with `appearance:none` and no
 * caret — so it read as a plain text input (founder, 2026-07-31), and a custom hex had NO visible way
 * back to a theme slot at all. It is now the house listbox (same chrome as `SelectControl`), always on.
 */
const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const CUSTOM = 'custom';
/**
 * The colour SOURCE for a field whose section derives the colour itself (`ColorField.auto`). Not a value:
 * picking it CLEARS the setting, and the section then paints from the theme / its colour scheme / the cell
 * the icon sits in. It exists so an automatic colour reads as a decision instead of an empty hex box.
 */
const AUTO = 'auto';

/** Merchant-facing names for the theme slots — "Accent 1" reads like the Theme settings panel, "accent" does not. */
const TOKEN_LABEL: Record<string, string> = {
  accent: 'Accent 1',
  'accent-2': 'Accent 2',
  bg: 'Background',
  surface: 'Surface',
  ink: 'Text',
  border: 'Border',
  success: 'Success',
  star: 'Star',
  outline: 'Outline button',
};

export function ColorControl({
  field,
  value,
  onChange,
}: {
  field: ColorField;
  value: ColorValue | undefined;
  onChange: (next: ColorValue | undefined) => void;
}) {
  // An `auto` field keeps its UNSET state visible: falling back to `field.default` here would paint a
  // source the seller never picked (and such a field carries no default anyway).
  const isAuto = field.auto === true && value === undefined;
  const current = isAuto ? undefined : (value ?? field.default);
  const isTheme = current?.ref === 'theme';
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pickerHex = current?.ref === 'custom' ? current.value : CUSTOM_FALLBACK_HEX;
  const [hex, setHex] = useState(pickerHex);

  useEffect(() => setHex(pickerHex), [pickerHex]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const summarySwatch =
    current?.ref === 'theme'
      ? resolveColor({ ref: 'theme', token: current.token })
      : current?.ref === 'custom'
        ? current.value
        : // Automatic (or a field with no default): the chip wears a neutral hatch (`--auto`, in shell.css)
          // so it reads as "the section decides" rather than as a colour that failed to load.
          undefined;
  const commitCustom = (v: string) => {
    setHex(v);
    if (HEX_RE.test(v)) onChange({ ref: 'custom', value: v });
  };
  // "Custom…" keeps the colour the merchant is looking at; a slot swaps the binding outright.
  const pickSource = (next: string) =>
    next === AUTO
      ? onChange(undefined)
      : next === CUSTOM
        ? onChange({
            ref: 'custom',
            value: isTheme ? themeTokenHex((current as { token: string }).token) : pickerHex,
          })
        : onChange({ ref: 'theme', token: next });

  return (
    <FieldRow id={field.id} label={field.label} info={field.info} labelAs="span" layout="row">
      <div className="bld-color" ref={ref}>
        <div className="bld-color__box">
          <button
            type="button"
            className={`bld-color__chipbtn${summarySwatch ? '' : ' bld-color__chipbtn--auto'}`}
            aria-label={btp('chrome.color.picker_aria', '{name} color picker', {
              name: field.label,
            })}
            aria-haspopup="dialog"
            aria-expanded={open}
            style={{ background: summarySwatch }}
            onClick={() => setOpen((v) => !v)}
          />
          <SourceListbox
            label={field.label}
            auto={field.auto === true}
            current={isAuto ? AUTO : isTheme ? (current as { token: string }).token : CUSTOM}
            onPick={pickSource}
          />
          {!isTheme && !isAuto && (
            <input
              className="bld-color__hexin"
              type="text"
              aria-label={btp('chrome.color.hex_aria', '{name} hex value', { name: field.label })}
              value={hex}
              placeholder={CUSTOM_FALLBACK_HEX}
              onChange={(e) => commitCustom(e.target.value)}
            />
          )}
        </div>

        {open && (
          <div
            className="bld-color__pop"
            role="dialog"
            aria-label={btp('chrome.color.pop_aria', '{name} picker', { name: field.label })}
          >
            <HsvColorPicker
              value={/^#[0-9a-f]{6}$/i.test(hex) ? hex : pickerHex}
              onChange={(h) => onChange({ ref: 'custom', value: h })}
              hexLabel={btp('chrome.color.pop_hex_aria', '{name} picker hex', {
                name: field.label,
              })}
            />
            <div className="bld-color__theme">
              <span className="bld-color__theme-label">{bt('chrome.color.theme', 'Theme')}</span>
              <div
                className="bld-swatches bld-swatches--mini"
                role="radiogroup"
                aria-label={btp('chrome.color.theme_aria', '{name} theme colors', {
                  name: field.label,
                })}
              >
                {THEME_COLOR_TOKENS.map((token) => {
                  const selected = current?.ref === 'theme' && current.token === token;
                  return (
                    <button
                      key={token}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      aria-label={token}
                      className="bld-swatch"
                      onClick={() => onChange({ ref: 'theme', token })}
                    >
                      <span
                        className="bld-swatch__chip"
                        style={{ background: resolveColor({ ref: 'theme', token }) }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </FieldRow>
  );
}

/**
 * The colour's SOURCE as the house listbox (never a native `<select>`, per the no-native-popup rule the
 * rest of the inspector follows). Sits inside the bordered colour box, so the row still reads as one
 * control: swatch · source · (hex, when custom).
 */
function SourceListbox({
  label,
  current,
  onPick,
  auto = false,
}: {
  label: string;
  current: string;
  onPick: (next: string) => void;
  /** Offer "Automatic" — the section computes this colour when the setting is left empty. */
  auto?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const options = [
    // First, because it is the state the field starts in.
    ...(auto ? [{ value: AUTO, label: bt('chrome.color.auto', 'Automatic') }] : []),
    ...THEME_COLOR_TOKENS.map((t) => ({
      value: t as string,
      // The map holds the English floor; the seller reads the token in her language (0051 lot 4).
      label: bt(`chrome.token.${t}`, TOKEN_LABEL[t] ?? t),
    })),
    { value: CUSTOM, label: bt('chrome.color.custom', 'Custom…') },
  ];
  const currentLabel =
    options.find((o) => o.value === current)?.label ?? bt('chrome.color.custom', 'Custom…');

  return (
    <div className="bld-color__source" ref={ref}>
      <button
        type="button"
        className="bld-color__sourcebtn"
        aria-label={btp('chrome.color.source_aria', '{name} color source', { name: label })}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <span className="bld-color__sourceval">{currentLabel}</span>
        <svg
          className="bld-listbox__caret"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5 6.5L8 3.5L11 6.5M5 9.5L8 12.5L11 9.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <ul
          className="bld-listbox__menu"
          role="listbox"
          aria-label={btp('chrome.color.source_aria', '{name} color source', { name: label })}
        >
          {options.map((o) => (
            <li
              key={o.value}
              role="option"
              aria-selected={o.value === current}
              className="bld-listbox__opt"
              onClick={() => {
                onPick(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
